<script setup lang="ts">
// Settings › Your data: export, import history, and moving servers.
import { ref } from 'vue'
import { describeError } from '../api/http'
import { moveWarning } from '../lib/memberStorage'
import { useSession } from '../stores/session'

const session = useSession()
const busy = ref(false)
const moveTo = ref('')
const moveCode = ref<string | null>(null)
const importFrom = ref('')
const importCode = ref('')
const historyInput = ref<HTMLInputElement | null>(null)
/** What moving away would remove here: my own libraries and storage (STORAGE.md §2.5). */
const moveLoses = ref<string | null>(null)

async function openedMove(open: boolean): Promise<void> {
  if (!open || !session.canLinkStorage) return
  try {
    moveLoses.value = moveWarning(await session.api.myStorage())
  } catch {
    // Not fatal: the move still works, just without the warning.
  }
}

async function download(): Promise<void> {
  busy.value = true
  try {
    const blob = await session.exportData()
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `hrth-export-${(session.me ?? 'me').replace(/[^a-z0-9.@-]/gi, '_')}-${new Date().toISOString().slice(0, 10)}.json`
    a.click()
    setTimeout(() => URL.revokeObjectURL(a.href), 10_000)
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    busy.value = false
  }
}

async function importHistory(file: File | undefined): Promise<void> {
  if (!file) return
  try {
    const n = await session.importHistory(file)
    session.toast(`Imported ${n} message${n === 1 ? '' : 's'}`)
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
}

async function startMove(): Promise<void> {
  try {
    moveCode.value = (await session.api.startMove(moveTo.value.trim().toLowerCase())).code
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
}

async function bringIn(): Promise<void> {
  busy.value = true
  try {
    const r = await session.api.importAccount(importFrom.value.trim().toLowerCase(), importCode.value.trim())
    await session.refresh()
    session.toast(`Moved in: ${r.spaces.length} space${r.spaces.length === 1 ? '' : 's'}`)
    importCode.value = ''
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <section class="space-y-3" data-testid="data-panel">
    <div class="label">Your data</div>
    <div class="flex flex-wrap gap-2">
      <button class="btn-ghost text-xs" :disabled="busy" data-testid="export-data" @click="download">↓ Download my data</button>
      <button class="btn-ghost text-xs" data-testid="import-history" @click="historyInput?.click()">↑ Import message history</button>
      <input ref="historyInput" type="file" accept="application/json" class="hidden" data-testid="history-input" @change="importHistory(($event.target as HTMLInputElement).files?.[0])" />
    </div>
    <p class="text-xs text-text-muted">
      The download includes your decrypted messages from this device — keep it somewhere safe. Import it on a new
      device to bring your history along.
    </p>
    <details class="text-sm" @toggle="openedMove(($event.target as HTMLDetailsElement).open)">
      <summary class="mono cursor-pointer tracking-[0.1em] text-text-secondary uppercase">Move to another server</summary>
      <div class="mt-2 space-y-2">
        <p class="text-xs text-text-muted">
          Create your account on the new server first, then enter its ID. Your spaces and roles here move to it.
        </p>
        <p v-if="moveLoses" class="border border-signal p-2 text-xs text-signal" data-testid="move-warning">{{ moveLoses }}</p>
        <div class="flex gap-2">
          <input v-model="moveTo" class="input mono" placeholder="you@new-server.example" data-testid="move-to" />
          <button class="btn-primary shrink-0 text-xs" :disabled="!moveTo.includes('@')" data-testid="start-move" @click="startMove">Get code</button>
        </div>
        <p v-if="moveCode" class="border border-border p-2 text-xs">
          On the new server open Settings › Bring in an old account and enter this code (valid 1 hour):
          <code class="mt-1 block font-mono break-all text-text-primary select-all" data-testid="move-code">{{ moveCode }}</code>
        </p>
      </div>
    </details>
    <details class="text-sm">
      <summary class="mono cursor-pointer tracking-[0.1em] text-text-secondary uppercase">Bring in an old account</summary>
      <div class="mt-2 space-y-2">
        <input v-model="importFrom" class="input mono" placeholder="you@old-server.example" data-testid="import-from" />
        <input v-model="importCode" class="input mono" placeholder="move code" data-testid="import-code" />
        <button class="btn-primary text-xs" :disabled="busy || !importFrom.includes('@') || !importCode" data-testid="bring-in" @click="bringIn">
          Move in
        </button>
      </div>
    </details>
  </section>
</template>
